import QRCode from 'react-qr-code';
import { styled } from 'styled-components';

import { getColor } from '../../../utils/getColor';
import { Assets } from '../../../assets/index';
import { theme } from '../../../styles/index';
import { ChevronIconOld, MenuCloseIcon } from '../../../assets/icons';
import { Button } from '../../Button';
import { Headings } from '../../Headings';
import { Flex, Spacer } from '../../Layout';
import { Text } from '../../Text';
import { useMediaMax, useMediaMin } from '../../Media';
import { ErrorAlert } from '../../ErrorAlert';

type BankIDProgressProps = {
  errorMessage: string;
  isDisabled: boolean;
  loginType: string;
  onCancel: () => void;
  onRetry: () => void;
  qrCodeValue: string;
  showErrorMessage: boolean;
};

const StyledQRCodeBorders = styled.div<{ color: string }>`
  --b: 6px; /* thickness of the border */
  --c: ${props => getColor(props.color || 'black90')}; /* color of the border */
  --w: 25px; /* width of border */
  --r: 10px; /* radius */

  position: relative;
  box-sizing: border-box;
  margin: 5px;
  margin-bottom: 28px;
  padding: 25px; /* space for the border */
  display: inline-flex;
  font-size: 30px;
  justify-content: center;
  align-items: center;
  text-align: center;
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--c, red);
    padding: var(--b);
    border-radius: var(--r);
    -webkit-mask:
      linear-gradient(0deg, #000 calc(2 * var(--b)), #0000 0) 50% var(--b) / calc(100% - 2 * var(--w)) 100% repeat-y,
      linear-gradient(-90deg, #000 calc(2 * var(--b)), #0000 0) var(--b) 50%/100% calc(100% - 2 * var(--w)) repeat-x,
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: destination-out;
    mask-composite: exclude;
  }
`;

export const BankIDProgress = ({
  errorMessage,
  isDisabled,
  loginType,
  onCancel,
  onRetry,
  qrCodeValue,
  showErrorMessage,
}: BankIDProgressProps) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  if (loginType === 'QR') {
    return (
      <BankIDQRProgress
        errorMessage={errorMessage}
        isDesktop={isDesktop}
        onCancel={onCancel}
        onRetry={onRetry}
        qrCodeValue={qrCodeValue}
        showErrorMessage={showErrorMessage}
      />
    );
  } else {
    return (
      <BankIDNativeProgress
        errorMessage={errorMessage}
        isDesktop={isDesktop}
        isDisabled={isDisabled}
        onCancel={onCancel}
        onRetry={onRetry}
        showErrorMessage={showErrorMessage}
      />
    );
  }
};

const BankIDQRProgress = ({
  errorMessage,
  isDesktop,
  onCancel,
  onRetry,
  qrCodeValue,
  showErrorMessage,
}: {
  errorMessage: string;
  isDesktop: boolean;
  onCancel: () => void;
  onRetry: () => void;
  qrCodeValue: string;
  showErrorMessage: boolean;
}) => {
  const isMobile = useMediaMax(theme.breakpoint.md);
  if (showErrorMessage) {
    return <RetryLoginButton onRetry={onRetry} errorMessage={errorMessage} isDesktop={isDesktop} />;
  }
  if (qrCodeValue) {
    if (isDesktop) {
      return (
        <Flex alignItems="flex-start" flexDirection="column" textAlign="center">
          <StyledQRCodeBorders color="foraBlue">
            <QRCode size={150} bgColor="white" fgColor="black" value={qrCodeValue} />
          </StyledQRCodeBorders>
          <Headings.H6 color="black90">Gör så här</Headings.H6>
          <QRInstructions />
          <CancelLoginButton onCancel={onCancel} isDesktop={isDesktop} styleAsLink />
        </Flex>
      );
    } else {
      return (
        <Flex alignItems="center" flexDirection="column" textAlign="left">
          <Headings.H4 marginTop={40} color="white" marginBottom={20}>
            Logga in med mobilt BankID
          </Headings.H4>
          <StyledQRCodeBorders color="skyBlue">
            <QRCode size={150} bgColor="transparent" fgColor="white" value={qrCodeValue} />
          </StyledQRCodeBorders>
          <Flex flexDirection="column" alignItems={isMobile ? 'flex-start' : 'center'}>
            <QRInstructions />
          </Flex>
          <CancelLoginButton onCancel={onCancel} isDesktop={isDesktop} styleAsLink={false} />
        </Flex>
      );
    }
  } else {
    // If we don't have a QR code value, we are still waiting for the backend to generate one.
    return (
      <Flex alignItems={isDesktop ? 'flex-start' : 'center'} flexDirection="column">
        {!isDesktop && (
          <Headings.H4 marginTop={40} color="white" marginBottom={20}>
            Logga in med mobilt BankID
          </Headings.H4>
        )}
        <Assets.Spinner
          color={isDesktop ? 'foraBlue' : 'white'}
          width={isDesktop ? 40 : 60}
          strokeWidth={isDesktop ? 4 : 6}
          data-testid="test-processing-in-progress-loading-element"
          marginBottom={20}
        />
        <CancelLoginButton onCancel={onCancel} isDesktop={isDesktop} styleAsLink />
      </Flex>
    );
  }
};

const QRInstructions = () => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  return (
    <>
      <Text marginTop={10} color={isDesktop ? 'black90' : 'white'}>
        1. Öppna BankID-appen.
      </Text>
      <Text color={isDesktop ? 'black90' : 'white'}>2. Tryck på QR-symbolen i BankID-appen.</Text>
      <Text color={isDesktop ? 'black90' : 'white'}>3. Rikta kameran mot QR-koden.</Text>
      <Text marginBottom={30} color={isDesktop ? 'black90' : 'white'}>
        4. Följ instruktioner i appen.
      </Text>
    </>
  );
};

const BankIDNativeProgress = ({
  errorMessage,
  isDesktop,
  isDisabled,
  onCancel,
  onRetry,
  showErrorMessage,
}: {
  errorMessage: string;
  isDesktop: boolean;
  isDisabled: boolean;
  onCancel: () => void;
  onRetry: () => void;
  showErrorMessage: boolean;
}) => {
  if (showErrorMessage) {
    return <RetryLoginButton errorMessage={errorMessage} isDesktop={isDesktop} onRetry={onRetry} />;
  }
  if (isDesktop) {
    return (
      <Flex alignItems="flex-start" flexDirection="column" textAlign="center">
        <Assets.Spinner
          marginBottom={[60, 40, 40]}
          color={'foraBlue'}
          width={50}
          strokeWidth={5}
          data-testid="test-native-in-progress-loading-element"
        />
        <CancelLoginButton onCancel={onCancel} isDesktop={isDesktop} styleAsLink />
      </Flex>
    );
  } else {
    return (
      <Flex
        position="relative"
        justifyContent="center"
        alignItems="center"
        paddingTop={80}
        paddingBottom={160}
        paddingRight={20}
        paddingLeft={20}
        height="100%"
      >
        <Flex alignItems="center" flexDirection="column" rowGap={40}>
          <Headings.H3 color="white">Var god vänta</Headings.H3>
          <Assets.Loading color="white" width={100} data-testid="test-native-in-progress-loading-element" />
          <CancelLoginButton onCancel={onCancel} isDesktop={isDesktop} styleAsLink />
        </Flex>
      </Flex>
    );
  }
};

const RetryLoginButton = ({
  onRetry,
  errorMessage,
  isDesktop,
}: {
  onRetry: () => void;
  errorMessage: string;
  isDesktop: boolean;
}) => {
  return (
    <Flex alignItems="flex-start" flexDirection="column" maxWidth={586} textAlign="center">
      <Spacer marginBottom={20}>
        <ErrorAlert text={errorMessage} />
      </Spacer>
      <Button
        testId="test-button-cancel-bank-id-progress"
        fontSize="medium"
        styleAs="link"
        variant="primary"
        onClick={onRetry}
      >
        <ChevronIconOld
          direction="left"
          variant="thin"
          color={isDesktop ? 'foraBlue' : 'white'}
          marginRight={5}
          title="Avbryt"
          containerSize={17}
        />
        <Text color={isDesktop ? 'foraBlue' : 'white'}> Försök igen </Text>
      </Button>
    </Flex>
  );
};

const CancelLoginButton = ({
  onCancel,
  isDesktop,
  styleAsLink = false,
}: {
  onCancel: () => void;
  isDesktop: boolean;
  styleAsLink: boolean;
}) => {
  return (
    <Button
      testId="test-button-cancel-bank-id-progress"
      fontSize="medium"
      styleAs={styleAsLink ? 'link' : 'button'}
      variant={isDesktop ? 'primary' : 'outline'}
      onClick={onCancel}
      style={{ backgroundColor: isDesktop ? 'foraBlue' : 'transparent', borderColor: isDesktop ? 'foraBlue' : 'white' }}
    >
      {isDesktop ? (
        <>
          <Text color={styleAsLink ? 'foraBlue' : 'white'}>Avbryt inloggningen</Text>
          <MenuCloseIcon color={styleAsLink ? 'foraBlue' : 'white'} marginLeft={5} title="Avbryt" containerSize={13} />
        </>
      ) : (
        <>
          <MenuCloseIcon color={'white'} marginRight={5} title="Avbryt" containerSize={13} />
          <Text color={'white'}>Avbryt inloggningen</Text>
        </>
      )}
    </Button>
  );
};
